import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { FormSkeleton } from './PageSkeletons'
import AnchoredPopover from './AnchoredPopover'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle2, AlertCircle, AlertTriangle, Loader2, Send, RotateCw,
  X, Search, ExternalLink, ChevronDown, ChevronRight,
  Bot, Maximize2, Clock, Upload, XCircle, FileText,
  FileSpreadsheet, ClipboardList, Activity, Sparkles, History, Layers,
  RefreshCw, Check,
} from 'lucide-react'
import PDFViewer from './PDFViewer'
import CostBreakdown from './CostBreakdown'
import RequestPanelTabs from './requestDetail/RequestPanelTabs'
import FieldsPanelHeader from './requestDetail/FieldsPanelHeader'
import ActivityLogPanel from './requestDetail/ActivityLogPanel'
import EditGate from './requestDetail/EditGate'
import { nsApi, requestsApi } from '../services/api'
import { useToast } from '../context/ToastContext'
import { useAuth } from '../context/AuthContext'
import { useDisplayPreferences } from '../context/DisplayPreferencesContext'
import { isReadOnlyUser } from '../utils/readerAccess'
import { formatAuditLogsForActivity } from '../utils/auditLog'
import { formatDateTime, formatTimeAgo } from '../utils/dateUtils'
import { isRequestLocked } from '../utils/requestStatus'
import { computePostGate, serverErrorFieldKeys, gateMessage } from '../utils/nsPostGate'
import useSegmentedTabIndicator from '../hooks/useSegmentedTabIndicator'
import OpCoBadge from './opco/OpCoBadge'
import { MoveOpCoButton, UnassignedOpCoBanner } from './opco/MoveOpCoDialog'

// ─── ReferenceFieldInput — generic entity resolution ─────────────────────────
//
// The generic counterpart of VendorField/CustomerField in NSRequestView.jsx:
// same discipline (keep the extracted text visible, only auto-apply a
// backend-confirmed near-exact match, otherwise require an explicit click),
// generalized to whatever lookup_entity the record type's field carries
// instead of being hardcoded to vendors/customers. Edit-mode gating is the
// caller's job (via EditGate) — this component is always interactive.

function ReferenceFieldInput({ lookupEntity, extractedValue, onResolved, onCleared, orgId, disabled, fieldKey, activeField, onActivate, fullOptions, presetResolved }) {
  // `presetResolved` is a selection the PARENT already made — today that means
  // a template default it filled a blank field with. Without it this component
  // would render empty while the payload carried the value, which is the
  // mismatch the whole defaults feature exists to avoid.
  const [query, setQuery] = useState(presetResolved?.name || extractedValue || '')
  const [resolved, setResolved] = useState(presetResolved || null)
  const [candidates, setCandidates] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)
  // Tracks the extractedValue this field last auto-resolved against — not
  // just "have we ever fetched" — because extractedValue can change after
  // mount (e.g. a real extraction landing after an earlier, incomplete
  // snapshot the component happened to render first). A mount-only fetch
  // would leave this field's resolved choice pointed at whatever text was
  // extracted first, forever, even once the real value arrives — which is
  // exactly how a stale "Genus Limited" match once rode along silently into
  // a NetSuite post for a document that was actually for "Nimans Limited".
  // Re-running auto-resolve whenever extractedValue actually changes fixes
  // that. Skipped once the user has manually picked, typed, or cleared a
  // value themselves — an incoming prop change must never silently
  // overwrite a deliberate user choice.
  const lastAutoValueRef = useRef(undefined)
  const userTouchedRef = useRef(false)
  const typeTimerRef = useRef(null)
  useEffect(() => () => clearTimeout(typeTimerRef.current), [])

  useEffect(() => {
    if (!presetResolved?.id) return
    // Same id but a real name replacing the "#id" placeholder still updates.
    setResolved(prev => (
      prev?.id === presetResolved.id && prev?.name === presetResolved.name ? prev : presetResolved
    ))
    setQuery(prev => (
      !prev || prev === `#${presetResolved.id}` ? presetResolved.name || '' : prev
    ))
  }, [presetResolved])

  useEffect(() => {
    if (!open) return
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [open])

  // Only one reference field's dropdown may be open at a time — each one is
  // absolute-positioned and does not reserve layout space, so two open at
  // once in this 2-column grid visually overlap the field(s) below them,
  // looking like one merged candidate list. Closing whichever isn't the
  // most recently activated field avoids that entirely.
  useEffect(() => {
    if (open && activeField !== fieldKey) setOpen(false)
  }, [activeField, fieldKey, open])

  useEffect(() => {
    if (userTouchedRef.current) return
    // A parent-supplied selection is already an answer; re-resolving the
    // extracted text over the top of it would undo it.
    if (presetResolved?.id) return
    if (!extractedValue || extractedValue === lastAutoValueRef.current) return
    lastAutoValueRef.current = extractedValue
    setQuery(extractedValue)
    setResolved(null)
    setCandidates([])
    setLoading(true)
    nsApi.resolveReference({ lookup_entity: lookupEntity, extracted_text: extractedValue, org_id: orgId })
      .then(res => {
        const d = res.data || {}
        if (d.matched) {
          setResolved({ id: d.ns_internal_id, name: d.value })
          onResolved(d.ns_internal_id, d.value)
        } else {
          setCandidates(d.candidates || [])
          // Don't pop the candidates list open while the field is disabled
          // (not in edit mode) — it renders as an apparently-clickable
          // dropdown that EditGate then silently blocks, which looks like a
          // bug rather than a lock.
          if (!disabled && (d.candidates || []).length > 0) { setOpen(true); onActivate?.(fieldKey) }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractedValue])

  // For a small, fully-loaded entity (locations, subsidiaries, terms,
  // inco_terms), browsing is a plain client-side filter over the whole
  // list — no server round trip, no minimum-length-to-search requirement,
  // and it can show every option immediately on focus with an empty query.
  function browseFullOptions(filterText) {
    const needle = (filterText || '').trim().toLowerCase()
    const matches = !needle
      ? fullOptions
      : fullOptions.filter(c => (c.name || '').toLowerCase().includes(needle))
    setCandidates(matches.slice(0, 50))
    setOpen(true)
    onActivate?.(fieldKey)
  }

  async function doSearch() {
    const trimmed = query.trim()
    if (fullOptions) { browseFullOptions(trimmed); return }
    return doSearchFor(trimmed)
  }

  async function doSearchFor(trimmed, { autoAccept = true } = {}) {
    if (trimmed.length < 2) return
    setLoading(true)
    try {
      const res = await nsApi.resolveReference({ lookup_entity: lookupEntity, extracted_text: trimmed, org_id: orgId })
      const d = res.data || {}
      if (d.matched && !autoAccept) {
        // Asked to show the matches, not to pick one: the exact match is
        // listed (first) with whatever else is close.
        const found = (d.candidates && d.candidates.length) ? d.candidates : [{ id: d.ns_internal_id, name: d.value, score: 1 }]
        setCandidates(found)
        setOpen(true)
        onActivate?.(fieldKey)
      } else if (d.matched) {
        setResolved({ id: d.ns_internal_id, name: d.value })
        onResolved(d.ns_internal_id, d.value)
        setOpen(false)
      } else {
        setCandidates(d.candidates || [])
        setOpen(true)
        onActivate?.(fieldKey)
      }
    } catch { /* leave candidates/resolved state as-is on a failed search */ } finally { setLoading(false) }
  }

  function handleSelect(c) {
    userTouchedRef.current = true
    setResolved({ id: c.id, name: c.name })
    setQuery(c.name || '')
    setCandidates([])
    setOpen(false)
    onResolved(c.id, c.name)
  }

  // The × un-selects the record but keeps its name in the box and opens the
  // matches for that name, so changing a value starts from what was there
  // instead of from an empty field.
  function handleClear() {
    userTouchedRef.current = true
    clearTimeout(typeTimerRef.current)
    const keep = (resolved?.name && !String(resolved.name).startsWith('#') ? resolved.name : query) || ''
    setResolved(null); setQuery(keep); setCandidates([])
    onCleared()
    if (fullOptions) { browseFullOptions(''); return }
    doSearchFor(keep.trim(), { autoAccept: false })
  }

  const isValid = !!resolved
  return (
    <div className="relative" ref={containerRef}>
      <div className={`flex items-center rounded-lg border text-sm bg-white dark:bg-gray-800 ${isValid ? 'border-green-400 dark:border-green-600' : (query || '').trim() ? 'border-amber-400 dark:border-amber-500' : 'border-gray-300 dark:border-gray-700'}`}>
        <input
          type="text" value={query}
          placeholder={`Search ${lookupEntity}…`}
          onChange={e => {
            const next = e.target.value
            userTouchedRef.current = true
            setQuery(next)
            if (resolved) { setResolved(null); onCleared() }
            if (fullOptions) { browseFullOptions(next); return }
            // Vendors, customers and items are too many to preload: search the
            // server as the user types instead of waiting for Enter.
            clearTimeout(typeTimerRef.current)
            if (next.trim().length >= 2) typeTimerRef.current = setTimeout(() => doSearchFor(next.trim()), 350)
          }}
          onKeyDown={e => { if (e.key === 'Enter') doSearch() }}
          onFocus={() => {
            // Always the full unfiltered list on focus/click — mirrors the
            // vendor field's "click and a dropdown of options appears"
            // behavior. Filtering by whatever text is already in the box
            // (e.g. an already-resolved value's name) would show just that
            // one match instead of letting the user browse or switch to a
            // different option, which is the whole point of a bounded list.
            if (disabled) return
            if (fullOptions) { browseFullOptions(''); return }
            if (candidates.length > 0) { setOpen(true); onActivate?.(fieldKey) }
          }}
          className="flex-1 px-3 py-2 bg-transparent focus:outline-none min-w-0 disabled:cursor-not-allowed"
        />
        {loading && <Loader2 className="h-4 w-4 text-gray-400 animate-spin mr-2 flex-shrink-0" />}
        {isValid && !loading && !disabled && <button onClick={handleClear} aria-label="Change selection" title="Change selection" className="p-1.5 mr-1 hover:text-red-500 text-gray-400 flex-shrink-0"><X className="h-3.5 w-3.5" /></button>}
        {!isValid && !loading && !disabled && (
          <button onClick={doSearch} disabled={!fullOptions && query.trim().length < 2} className="p-1.5 mr-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex-shrink-0 disabled:opacity-40">
            <Search className="h-3.5 w-3.5 text-gray-400" />
          </button>
        )}
      </div>
      {isValid && (
        <p className="mt-1 text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
          <span className="font-mono font-semibold">ID:{resolved.id}</span>
          <span className="text-gray-400">—</span><span>{resolved.name}</span>
        </p>
      )}
      {!isValid && (query || '').trim() && !loading && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          Not matched — search and select the correct record
        </p>
      )}
      {open && candidates.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-xl max-h-56 overflow-y-auto">
          {candidates.map(c => (
            <button key={c.id} onClick={() => handleSelect(c)} className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{c.name}</span>
              {!fullOptions && (
                <span className="ml-2 text-xs text-gray-400 font-mono flex-shrink-0">{Math.round((c.score || 0) * 100)}%</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── NSGenericRequestView ────────────────────────────────────────────────────
//
// The review/post screen for ANY NetSuite document type that isn't Purchase
// Order or Vendor Bid — those two keep their own hand-built, more polished
// screens (NSRequestView/NSBidRequestView) unchanged. This one is driven
// entirely by the template's own mapped fields (request.fields) plus the
// record type's validation bundle (mandatory/reference/line-shape info,
// fetched the same way NSRequestView/NSBidRequestView already do via
// nsApi.getLookups) — never a hardcoded field list, so a newly
// self-service-configured record type renders correctly with no new code.
//
// The screen chrome (header bar, split fields/document layout, tabs, per-field
// History/Alternatives/view-in-document) mirrors NSRequestView.jsx exactly —
// only the field-driving logic below stays generic/config-driven.

const ARRAY_FIELD_TYPES = new Set(['array_of_objects', 'table'])

// D/M/YYYY -> YYYY-MM-DD for display in an <input type="date">; anything else
// is returned unchanged. Mirrors NetSuiteService._to_iso_date.
function toIsoDateForInput(value) {
  if (typeof value !== 'string') return value ?? ''
  const m = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (!m) return value
  const [, day, month, year] = m
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

export default function NSGenericRequestView({ request, showProcessingModal, processingMinimized, setProcessingMinimized, processingProgress = { percent: 0, message: '' }, processingLog = [], onExtract, onOpCoMoved }) {
  const toast = useToast()
  const navigate = useNavigate()
  const { user: authUser } = useAuth()
  const isReader = isReadOnlyUser(authUser)
  const { showConfidenceScore, showAiBadge, showColorCoding } = useDisplayPreferences()

  const recordTypeKey = request?.template_ns_entity_type || ''
  const orgId = request?.organization_id || ''
  const displayLabel = request?.template_ns_entity_display_name || recordTypeKey

  const fields = useMemo(() =>
    (request?.fields || []).filter(f => f.is_active !== false).map(f => ({
      ...f,
      value: f.value ?? f.field_value ?? '',
      display_name: f.display_name || f.template_field?.display_name || f.field_name || '',
      ns_field: f.ns_field ?? f.template_field?.ns_field ?? null,
      is_required: f.is_required || f.template_field?.is_required || false,
      field_type: f.field_type || f.template_field?.field_type || f.template_field?.data_type || 'text',
      // For the line-items table field: its JSON column definitions, each
      // carrying its own ns_field / posted / required / inherited_from_header.
      field_values: f.field_values ?? f.template_field?.field_values ?? null,
    }))
  , [request])

  const [validation, setValidation] = useState(null)
  const [loadingValidation, setLoadingValidation] = useState(true)

  // The header key an embedded record type's lines post under (e.g. `item` on
  // a Purchase Order). It is a CONTAINER, not a value: rendering it as an
  // ordinary header input showed a text box holding one line's description
  // and would have posted a string where NetSuite expects an array of lines.
  //
  // Careful: on a Purchase Order the same name `item` is ALSO a real column on
  // each line (the item reference). So the container must only be suppressed
  // where it is acting as a container — a LINE-scoped field of the same name
  // is a genuine column and one of the most important ones on the record.
  const lineContainerKey = validation?.line_container_key || null
  const rawScopeOf = useCallback(
    (ns_field) => (validation?.field_scope_map || {})[ns_field] || 'header',
    [validation]
  )

  // A field mapped onto the container but not line-scoped is a mapping made
  // before embedded lines were understood: it holds the raw extracted blob
  // rather than a column, so it seeds the rows and is never a header input.
  const staleContainerField = useMemo(
    () => (lineContainerKey
      ? fields.find(f => f.ns_field === lineContainerKey && rawScopeOf(f.ns_field) !== 'line')
      : undefined),
    [fields, lineContainerKey, rawScopeOf]
  )

  const lineTableField = useMemo(
    () => fields.find(f => ARRAY_FIELD_TYPES.has(f.field_type)) || staleContainerField,
    [fields, staleContainerField]
  )
  const headerFieldDefs = useMemo(
    () => fields.filter(f => f !== lineTableField && f.ns_field),
    [fields, lineTableField]
  )

  // Full option lists for small, bounded lookup entities (locations,
  // subsidiaries, terms, inco_terms — a couple hundred rows at most) so
  // those fields can show a real dropdown of everything immediately instead
  // of forcing a type-to-search round trip. Left out for vendors/items
  // (1,000+ records) — those stay search-driven, a plain dropdown would be
  // unusable at that size.
  const [fullLookupOptions, setFullLookupOptions] = useState({})
  useEffect(() => {
    let cancelled = false
    nsApi.getLookups(request?.template_id)
      .then(res => {
        if (cancelled) return
        setValidation(res.data?.validation || null)
        const d = res.data || {}
        setFullLookupOptions({
          locations: d.locations || [],
          subsidiaries: d.subsidiaries || [],
          currencies: d.currencies || [],
          terms: d.terms || [],
          inco_terms: d.inco_terms || [],
          shipping_terms: d.shipping_terms || [],
        })
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingValidation(false) })
    return () => { cancelled = true }
  }, [request?.template_id])

  const scopeOf = useCallback((ns_field) => (validation?.field_scope_map || {})[ns_field] || 'header', [validation])
  const labelFor = useCallback((f) => (validation?.field_labels || {})[f.ns_field] || f.display_name || f.field_name || f.ns_field, [validation])
  // A reference is either a lookup-backed field (reference_fields) or one
  // NetSuite/the template declares as a reference (field_input_types).
  const isReferenceField = useCallback((ns_field) =>
    (validation?.reference_fields || []).includes(ns_field) ||
    (validation?.field_input_types || {})[ns_field] === 'reference'
  , [validation])
  const lookupEntityFor = useCallback((ns_field) => (validation?.lookup_entity_map || {})[ns_field], [validation])
  // The search picker needs a lookup collection to search. A reference with
  // none (a custom-record reference NetSuite gives no lookup for) keeps a
  // plain input holding the internal id, which the server wraps as {id}.
  const usesPicker = useCallback((ns_field) => isReferenceField(ns_field) && !!lookupEntityFor(ns_field), [isReferenceField, lookupEntityFor])
  const fullOptionsFor = useCallback((ns_field) => fullLookupOptions[lookupEntityFor(ns_field)] || null, [fullLookupOptions, lookupEntityFor])

  // Line-scope views. A key can be a field on the header AND on every line
  // with different meaning (e.g. `location`); the bundle keeps the line rules
  // separately so a line column never borrows the header field's shape.
  const lineLookupEntityFor = useCallback((ns_field) =>
    (validation?.line_lookup_entity_map || {})[ns_field] ?? (validation?.lookup_entity_map || {})[ns_field]
  , [validation])
  const isLineReferenceField = useCallback((ns_field) =>
    Array.isArray(validation?.line_reference_fields)
      ? validation.line_reference_fields.includes(ns_field)
      : isReferenceField(ns_field)
  , [validation, isReferenceField])
  const lineUsesPicker = useCallback((ns_field) => isLineReferenceField(ns_field) && !!lineLookupEntityFor(ns_field), [isLineReferenceField, lineLookupEntityFor])
  const lineFullOptionsFor = useCallback((ns_field) => fullLookupOptions[lineLookupEntityFor(ns_field)] || null, [fullLookupOptions, lineLookupEntityFor])
  const lineLabelFor = useCallback((f) =>
    (validation?.line_field_labels || {})[f.ns_field] || (f._column ? f.display_name : null) || labelFor(f)
  , [validation, labelFor])

  // How NetSuite itself describes this field, so a checkbox field renders as
  // a checkbox rather than a text box the reviewer has to type "true" into.
  // Falls back to the template field's own data type, which is what this
  // screen used for every field before the bundle carried NetSuite's view.
  const inputTypeFor = useCallback((field) => {
    const fromBundle = (validation?.field_input_types || {})[field?.ns_field]
    if (fromBundle) return fromBundle
    if (field?.field_type === 'date') return 'date'
    if (field?.field_type === 'number') return 'number'
    return 'text'
  }, [validation])

  // Template defaults, as resolved by the server for THIS record type
  // (bundle.template_defaults — empty unless the template has opted in).
  //
  // These are applied here in the UI as well as at post time, and that is
  // deliberate rather than redundant: the server fills a blank field on the
  // way to NetSuite, so if the review screen did not also show it, the user
  // would approve a visibly empty field and a value they never saw would be
  // posted. Seeding here means what is on screen is what gets sent, and the
  // server-side pass becomes the guarantee that agrees with it rather than a
  // surprise.
  const templateDefaults = useMemo(() => validation?.template_defaults || {}, [validation])
  const [defaultedFields, setDefaultedFields] = useState(() => new Set())
  const isDefaulted = useCallback((ns_field) => defaultedFields.has(ns_field), [defaultedFields])

  const headerFields = useMemo(() => headerFieldDefs.filter(f => scopeOf(f.ns_field) === 'header'), [headerFieldDefs, scopeOf])
  // Line columns come from the line-items table field's own column
  // definitions first: each column names its NetSuite key (ns_field) and
  // whether it posts at all (posted:false = an extraction-only helper, never
  // a NetSuite column). Templates built before per-column mappings existed
  // map line columns as separate line-scoped template fields instead — those
  // still count, for any NetSuite key no column already claims.
  const lineColumnDefs = useMemo(() => {
    let raw = lineTableField?.field_values
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw) } catch { raw = null }
    }
    if (!Array.isArray(raw)) return []
    const seen = new Set()
    const out = []
    for (const col of raw) {
      if (!col || typeof col !== 'object') continue
      if (col.posted === false || !col.ns_field) continue
      if (seen.has(col.ns_field)) continue
      seen.add(col.ns_field)
      out.push({
        _column: true,
        id: `col-${col.key || col.name || col.ns_field}`,
        ns_field: col.ns_field,
        field_name: col.key || col.name || col.ns_field,
        column_key: col.key || col.name || '',
        column_name: col.name || '',
        display_name: col.label || col.name || col.key || col.ns_field,
        is_required: !!col.required,
        inherited_from_header: col.inherited_from_header || null,
      })
    }
    return out
  }, [lineTableField])
  const lineFieldDefs = useMemo(() => {
    const fromColumns = new Set(lineColumnDefs.map(c => c.ns_field))
    const legacy = headerFieldDefs.filter(f => scopeOf(f.ns_field) === 'line' && !fromColumns.has(f.ns_field))
    return [...lineColumnDefs, ...legacy]
  }, [lineColumnDefs, headerFieldDefs, scopeOf])
  const hasLineItems = !!validation?.has_line_items

  // This record type takes line items, but no field on this template is
  // mapped to a line column — so there is nothing to build a row out of.
  // Distinguished from "no lines added" because the two need opposite
  // actions: this one can only be fixed in the template's field mapping,
  // never by the reviewer clicking "Add line" on this screen.
  const lineColumnsUnmapped = hasLineItems && lineFieldDefs.length === 0

  const [headerValues, setHeaderValues] = useState({})
  const [resolvedRefs, setResolvedRefs] = useState({}) // ns_field -> {id, name}
  // Same class of bug as the line-items race and ReferenceFieldInput's own
  // stale-match issue: a one-shot `seeded` boolean would lock these plain
  // text values in against whatever `headerFields` looked like on the
  // FIRST run and never notice a later, more complete/corrected extraction
  // landing on the same mounted component. Re-seed whenever the underlying
  // extracted values actually change instead, but never clobber a field the
  // user has already typed into themselves.
  const lastSeededSigRef = useRef(undefined)
  const touchedHeaderFieldsRef = useRef(new Set())
  const defaultNameRequestsRef = useRef(new Set()) // "field:id" already asked for its name
  useEffect(() => {
    if (headerFieldDefs.length === 0) return
    // The signature has to cover the defaults as well as the extracted values:
    // `validation` resolves on its own, later promise, so seeding keyed only on
    // field values returns early the moment the defaults actually arrive.
    const sig = JSON.stringify([
      headerFields.map(f => [f.ns_field, f.value]),
      Object.entries(templateDefaults).map(([k, v]) => [k, v?.value]),
    ])
    if (sig === lastSeededSigRef.current) return
    lastSeededSigRef.current = sig
    setHeaderValues(prev => {
      const hv = { ...prev }
      const filledFromDefault = new Set()
      for (const f of headerFields) {
        if (touchedHeaderFieldsRef.current.has(f.ns_field)) continue
        const extracted = f.value ?? ''
        // A default only ever fills a blank — it is what to use when the
        // document did not say, never an override of what it did say. Same
        // rule the server applies, so the two cannot disagree.
        const fallback = templateDefaults[f.ns_field]
        if (String(extracted).trim() === '' && fallback?.value != null) {
          hv[f.ns_field] = fallback.value
          filledFromDefault.add(f.ns_field)
        } else {
          hv[f.ns_field] = extracted
        }
      }
      setDefaultedFields(filledFromDefault)
      return hv
    })
  }, [headerFieldDefs, headerFields, templateDefaults])

  // Line items: parsed from the single array-typed extraction field, one row
  // per extracted row, columns matched to a configured line field by exact
  // or case-insensitive key/label match — a generic type has no hardcoded
  // alias table the way PO/Bid's line parsers do, so this is best-effort;
  // every value stays directly editable in the table below regardless.
  const [lineRows, setLineRows] = useState([])
  // Tracks the raw field value this was last seeded from — not just a
  // "seeded once" boolean — because `hasLineItems` (from the separate
  // validation fetch) can turn true before `request.fields` has caught up
  // with real extracted data (a genuine race between two independent
  // fetches, not merely "not loaded yet"). A one-shot boolean guard would
  // permanently lock in an empty blank row from that first, premature run
  // and never re-seed once the real data actually arrives. Re-seeding
  // whenever the underlying value string changes fixes that, and still
  // never clobbers in-progress edits once real data has already landed.
  const lastSeededValueRef = useRef(undefined)
  useEffect(() => {
    if (!hasLineItems) return
    const currentValue = lineTableField?.value
    if (currentValue === lastSeededValueRef.current) return
    lastSeededValueRef.current = currentValue
    let raw = []
    if (currentValue) {
      try {
        const parsed = typeof currentValue === 'string' ? JSON.parse(currentValue) : currentValue
        if (Array.isArray(parsed)) raw = parsed
      } catch { /* fall through to a single blank row */ }
    }
    const blankRow = () => Object.fromEntries(lineFieldDefs.map(f => [f.ns_field, '']))
    if (raw.length === 0) { setLineRows([blankRow()]); return }
    const normKey = (k) => String(k ?? '').toLowerCase().trim().replace(/[\s.-]+/g, '_')
    setLineRows(raw.map(extractedRow => {
      const normEntries = Object.entries(extractedRow || {}).map(([k, v]) => [normKey(k), v])
      const norm = Object.fromEntries(normEntries)
      const row = {}
      for (const f of lineFieldDefs) {
        // A mapped column is read by its OWN extraction key (the column's
        // key/name) — that is the name the extractor wrote the value under.
        // The NetSuite key / label match stays as the fallback, and is the
        // only match a legacy line-scoped template field has.
        const fromColumn = f._column
          ? (extractedRow?.[f.column_key] ?? norm[normKey(f.column_key)] ?? norm[normKey(f.column_name)])
          : undefined
        const label = normKey(labelFor(f))
        const key = f.ns_field.toLowerCase()
        row[f.ns_field] = fromColumn ?? norm[key] ?? norm[label] ?? norm[normKey(f.display_name)] ?? ''
      }
      return row
    }))
  }, [hasLineItems, lineTableField, lineFieldDefs, labelFor])

  // inherited_from_header: a column whose value the document prints ONCE in
  // its header. A line that leaves it blank takes the header field of that
  // name (the reviewer's current value when it is an edited NetSuite field,
  // otherwise its extracted value). Resolved at payload time so a header edit
  // flows onto every line that inherits it.
  const inheritedValueFor = useCallback((f) => {
    const headerName = f?.inherited_from_header
    if (!headerName) return undefined
    const source = fields.find(h => h !== lineTableField && (h.field_name === headerName || h.ns_field === headerName))
    if (!source) return undefined
    if (source.ns_field) {
      if (usesPicker(source.ns_field)) {
        const r = resolvedRefs[source.ns_field]
        if (r?.id) return { id: r.id, name: r.name }
      }
      const edited = headerValues[source.ns_field]
      if (edited !== undefined && edited !== null && String(edited).trim() !== '') return edited
    }
    const extracted = source.value
    return (extracted !== undefined && extracted !== null && String(extracted).trim() !== '') ? extracted : undefined
  }, [fields, lineTableField, usesPicker, headerValues, resolvedRefs])

  const [posting, setPosting] = useState(false)
  const [postResult, setPostResult] = useState(null)
  // The record this request was last posted to, read from the post log, so
  // "Open in NetSuite" survives a reload. Re-read after every post attempt.
  const [postedRecord, setPostedRecord] = useState(null)
  useEffect(() => {
    if (!request?.id) return undefined
    let cancelled = false
    Promise.resolve(nsApi.getPostedRecord?.(request.id))
      .then(res => { if (!cancelled) setPostedRecord(res?.data?.posted ? res.data : null) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [request?.id, postResult])
  // Which reference field's dropdown (if any) is allowed to be open — see
  // ReferenceFieldInput's own comment on why only one may be open at once.
  const [activeReferenceField, setActiveReferenceField] = useState(null)

  const setHeaderValue = (ns_field, v) => {
    touchedHeaderFieldsRef.current.add(ns_field)
    setHeaderValues(prev => ({ ...prev, [ns_field]: v }))
    // Once the user has typed here the value is theirs, not the template's,
    // and the badge would be a lie.
    setDefaultedFields(prev => {
      if (!prev.has(ns_field)) return prev
      const next = new Set(prev)
      next.delete(ns_field)
      return next
    })
  }
  const setLineValue = (idx, ns_field, v) => setLineRows(prev => prev.map((r, i) => i === idx ? { ...r, [ns_field]: v } : r))

  const buildHeaderPayload = useCallback(() => {
    const p = {}
    for (const f of headerFields) {
      const key = f.ns_field
      if (usesPicker(key)) {
        const r = resolvedRefs[key]
        if (r?.id) p[key] = { id: r.id }
        continue
      }
      const v = headerValues[key]
      if (v !== undefined && v !== null && String(v).trim() !== '') p[key] = v
    }
    return p
  }, [headerFields, headerValues, resolvedRefs, usesPicker])

  const buildLinePayload = useCallback(() => {
    if (!hasLineItems) return []
    // A resolved reference cell holds {id, name} (see the line-items table's
    // ReferenceFieldInput) — only `id` is ever sent to NetSuite, `name` was
    // just there so the search box could keep showing what was picked.
    const hasValue = (v) => (v && typeof v === 'object') ? !!v.id : String(v ?? '').trim() !== ''
    const inheriting = lineFieldDefs.filter(f => f.inherited_from_header)
    return lineRows
      // A row is real only if the DOCUMENT gave it a value — inherited
      // header values alone must not turn a blank row into a posted line.
      .filter(r => Object.values(r).some(hasValue))
      .map(r => {
        const clean = {}
        for (const [k, v] of Object.entries(r)) {
          if (!hasValue(v)) continue
          clean[k] = (v && typeof v === 'object') ? { id: v.id } : v
        }
        for (const f of inheriting) {
          if (hasValue(clean[f.ns_field])) continue
          const inherited = inheritedValueFor(f)
          if (!hasValue(inherited)) continue
          clean[f.ns_field] = (inherited && typeof inherited === 'object') ? { id: inherited.id } : inherited
        }
        return clean
      })
  }, [hasLineItems, lineRows, lineFieldDefs, inheritedValueFor])

  const missingRequired = useMemo(() => {
    const out = []
    for (const f of headerFields) {
      if (!f.is_required) continue
      const key = f.ns_field
      const val = usesPicker(key) ? resolvedRefs[key]?.id : headerValues[key]
      if (val === undefined || val === null || String(val).trim() === '') out.push({ label: labelFor(f), key })
    }
    return out
  }, [headerFields, headerValues, resolvedRefs, usesPicker, labelFor])

  // ── Authoritative pre-post validation ────────────────────────────────
  // `missingRequired` above only knows whether a box looks empty. The
  // backend's validate_before_post is the real gate: it checks date
  // formats, line numerics, reference resolvability and duplicate posts.
  // Enabling Post on emptiness alone let reviewers fire off documents
  // NetSuite was always going to reject, turning a fixable validation
  // message into a failed post they had to chase in the log.
  const [serverValidation, setServerValidation] = useState({ status: 'idle', errors: [] })
  const validationSeq = useRef(0)

  const validationSignature = useMemo(
    () => JSON.stringify([headerValues, resolvedRefs, lineRows]),
    [headerValues, resolvedRefs, lineRows]
  )

  useEffect(() => {
    if (!request?.id || !orgId) return undefined
    // Don't spend NetSuite round trips while required boxes are still
    // empty — those are already flagged red and block Post on their own.
    if (missingRequired.length > 0) {
      setServerValidation({ status: 'blocked_local', errors: [] })
      return undefined
    }
    const seq = ++validationSeq.current
    setServerValidation(prev => ({ ...prev, status: 'checking' }))
    const timer = setTimeout(async () => {
      try {
        const res = await nsApi.validate({
          request_id: request.id,
          org_id: orgId,
          ns_entity_type: recordTypeKey,
          payload: buildHeaderPayload(),
          line_items: buildLinePayload(),
        })
        if (seq !== validationSeq.current) return
        const data = res?.data || {}
        setServerValidation({
          status: data.valid ? 'valid' : 'invalid',
          errors: Array.isArray(data.errors) ? data.errors : [],
          alreadyPosted: !!data.already_posted,
        })
      } catch {
        if (seq !== validationSeq.current) return
        // Fail OPEN when we cannot reach the checker at all. A flaky
        // network must not permanently disable Post; the post path runs
        // the same validation server-side, so a genuinely invalid
        // payload is still refused there.
        setServerValidation({ status: 'unavailable', errors: [] })
      }
    }, 600)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationSignature, missingRequired.length, request?.id, orgId, recordTypeKey])

  const serverFieldErrors = useMemo(() => serverErrorFieldKeys(serverValidation), [serverValidation])

  const postGate = useMemo(() => computePostGate({
    missingRequired,
    posting,
    hasLineItems,
    lineCount: hasLineItems ? buildLinePayload().length : 0,
    serverValidation,
  }), [missingRequired, posting, hasLineItems, buildLinePayload, serverValidation])

  const canPost = postGate.canPost

  const handlePost = useCallback(async () => {
    if (!canPost) return
    setPosting(true)
    setPostResult(null)
    try {
      const res = await nsApi.createNsRecord(recordTypeKey, {
        request_id: request.id, org_id: orgId,
        header_fields: buildHeaderPayload(),
        line_items: buildLinePayload(),
      })
      setPostResult(res.data)
      if (res.data?.success) {
        toast.success(res.data?.updated ? 'Correction applied in NetSuite' : 'Posted to NetSuite')
        await requestsApi.updateStatus(request.id, 'completed').catch(() => {})
        onExtract?.()
      } else if (res.data?.partial) {
        toast.error(`Partially posted — ${res.data.lines_created} of ${res.data.lines_total} lines`)
      } else {
        toast.error(res.data?.error || 'Failed to post to NetSuite')
      }
    } catch (err) {
      const data = err?.response?.data
      setPostResult(data || { success: false, error: 'Failed to post to NetSuite' })
      toast.error(data?.error || 'Failed to post to NetSuite')
    } finally {
      setPosting(false)
    }
  }, [canPost, recordTypeKey, request?.id, orgId, buildHeaderPayload, buildLinePayload, toast, onExtract])

  const handleRetry = useCallback(async () => {
    setPosting(true)
    try {
      // Retry what is on screen NOW — the same header/lines the initial post
      // sends. Sending nothing made the server fall back to the failed
      // attempt's logged payload, silently discarding every correction made
      // since (usually the very fix the retry was for).
      const res = await nsApi.retryNsRecord(recordTypeKey, {
        request_id: request.id, org_id: orgId,
        header_fields: buildHeaderPayload(),
        line_items: buildLinePayload(),
        approved_by: authUser?.email || authUser?.username || authUser?.name || undefined,
      })
      setPostResult(res.data)
      if (res.data?.success) {
        toast.success('Posted to NetSuite')
        await requestsApi.updateStatus(request.id, 'completed').catch(() => {})
        onExtract?.()
      } else {
        toast.error(res.data?.error || 'Retry failed')
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Retry failed')
    } finally {
      setPosting(false)
    }
  }, [recordTypeKey, request?.id, orgId, toast, onExtract, buildHeaderPayload, buildLinePayload, authUser])

  useEffect(() => {
    const entries = Object.entries(templateDefaults)
    if (entries.length === 0) return
    setResolvedRefs(prev => {
      let changed = false
      const next = { ...prev }
      const filled = []
      for (const [fieldKey, meta] of entries) {
        if (!usesPicker(fieldKey)) continue
        if (touchedHeaderFieldsRef.current.has(fieldKey)) continue
        if (next[fieldKey]?.id) continue          // extraction already resolved it
        const id = String(meta?.value ?? '').trim()
        if (!id) continue
        const options = fullLookupOptions[meta.lookup_entity] || []
        const match = options.find(o => String(o.id) === id)
        // Show the id alone when the collection has not loaded yet rather than
        // skipping the field — the posted value is the id either way, and a
        // visible "#7" beats a field that looks empty.
        next[fieldKey] = { id, name: match?.name || `#${id}` }
        filled.push(fieldKey)
        changed = true
      }
      if (filled.length) {
        setDefaultedFields(prevSet => {
          const nextSet = new Set(prevSet)
          filled.forEach(k => nextSet.add(k))
          return nextSet
        })
      }
      return changed ? next : prev
    })
    // Collections too big to preload (vendors, customers, items) leave the
    // default showing as "#id"; ask the server for that one record's name.
    for (const [fieldKey, meta] of entries) {
      const id = String(meta?.value ?? '').trim()
      if (!id || !usesPicker(fieldKey) || fullLookupOptions[meta.lookup_entity]) continue
      const askKey = `${fieldKey}:${id}`
      if (defaultNameRequestsRef.current.has(askKey)) continue
      defaultNameRequestsRef.current.add(askKey)
      nsApi.resolveReference({ lookup_entity: meta.lookup_entity, extracted_text: id, by_id: true, org_id: orgId })
        .then(res => {
          const name = res?.data?.matched ? res.data.value : null
          if (!name) return
          setResolvedRefs(prev => (
            prev[fieldKey]?.id === id && prev[fieldKey]?.name === `#${id}`
              ? { ...prev, [fieldKey]: { id, name } }
              : prev
          ))
        })
        .catch(() => {})
    }
  }, [templateDefaults, fullLookupOptions, usesPicker, orgId])

  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelling, setCancelling] = useState(false)

  const handleCancel = useCallback(async () => {
    if (!cancelReason.trim()) return
    setCancelling(true)
    try {
      await requestsApi.cancel(request.id, cancelReason)
      setShowCancelModal(false)
      navigate(-1)
    } catch {
      toast.error('Failed to cancel request')
    } finally {
      setCancelling(false)
    }
  }, [cancelReason, request?.id, navigate, toast])

  // ── Field → document highlight, History + Alternatives popups ─────────────
  const [highlightedField, setHighlightedField] = useState(null)

  // Scroll-to + pulse-highlight a field row when a missing-required chip
  // referencing it is clicked — same click-to-jump behaviour as the other
  // request views' checklist cards.
  const fieldRowRefs = useRef({})
  const [pulsedFieldKey, setPulsedFieldKey] = useState(null)
  const fieldJumpCycleRef = useRef({})
  const [showChecklist, setShowChecklist] = useState(false)

  const jumpToFieldKeys = (keys) => {
    const validKeys = (keys || []).filter(Boolean)
    if (validKeys.length === 0) return
    const groupId = validKeys.join('|')
    const idx = (fieldJumpCycleRef.current[groupId] || 0) % validKeys.length
    fieldJumpCycleRef.current[groupId] = idx + 1
    const key = validKeys[idx]
    const el = fieldRowRefs.current[key]
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setPulsedFieldKey(key)
    setTimeout(() => setPulsedFieldKey(k => (k === key ? null : k)), 1800)
  }

  const jumpHighlightClass = (key) =>
    pulsedFieldKey === key
      ? 'rounded-lg ring-2 ring-red-400 dark:ring-red-500 ring-offset-2 ring-offset-white dark:ring-offset-gray-900 animate-pulse'
      : ''

  const [openAlternativesFieldId, setOpenAlternativesFieldId] = useState(null)
  const [alternatives, setAlternatives] = useState([])
  const [loadingAlternatives, setLoadingAlternatives] = useState(false)
  const alternativesPopupRef = useRef(null)

  const [openAuditFieldId, setOpenAuditFieldId] = useState(null)
  const [fieldAuditLogs, setFieldAuditLogs] = useState([])
  const [loadingFieldAudit, setLoadingFieldAudit] = useState(false)
  const auditPopupRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (alternativesPopupRef.current && !alternativesPopupRef.current.contains(event.target)) {
        setOpenAlternativesFieldId(null)
      }
      if (auditPopupRef.current && !auditPopupRef.current.contains(event.target)) {
        setOpenAuditFieldId(null)
      }
    }
    if (openAlternativesFieldId != null || openAuditFieldId != null) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openAlternativesFieldId, openAuditFieldId])

  const handleViewFieldInDocument = (field) => {
    const hasValue = !!(field?.value ?? '').toString().trim()
    if (!hasValue) {
      setHighlightedField(null)
      return
    }
    setHighlightedField({
      bounding_box: field.bounding_box,
      page_number: field.page_number,
      field_name: field.field_name,
      extracted_value: field.value,
    })
  }

  const handleToggleAlternatives = async (field) => {
    if (openAlternativesFieldId === field.id) {
      setOpenAlternativesFieldId(null)
      return
    }
    setOpenAuditFieldId(null)
    setOpenAlternativesFieldId(field.id)
    setLoadingAlternatives(true)
    try {
      const res = await requestsApi.getFieldAlternatives(request.id, field.field_name)
      setAlternatives(res.data.alternatives || [])
    } catch (err) {
      console.error('Error loading field alternatives:', err)
      setAlternatives([])
    } finally {
      setLoadingAlternatives(false)
    }
  }

  const handleSelectAlternative = (field, alt) => {
    const newValue = alt.field_value || alt.extracted_value || ''
    setHeaderValue(field.ns_field, newValue)
    setOpenAlternativesFieldId(null)
    if (alt.bounding_box || alt.page_number) {
      setHighlightedField({
        bounding_box: alt.bounding_box,
        page_number: alt.page_number,
        field_name: field.field_name,
        extracted_value: newValue,
      })
    }
  }

  const handleToggleFieldAudit = async (field) => {
    if (openAuditFieldId === field.id) {
      setOpenAuditFieldId(null)
      return
    }
    setOpenAlternativesFieldId(null)
    setOpenAuditFieldId(field.id)
    setLoadingFieldAudit(true)
    try {
      const res = await requestsApi.getFieldAuditLogs(request.id, field.id, { limit: 20 })
      setFieldAuditLogs(res.data.audit_logs || [])
    } catch (err) {
      console.error('Error loading field audit log:', err)
      setFieldAuditLogs([])
    } finally {
      setLoadingFieldAudit(false)
    }
  }

  // ── Split layout + tabs ─────────────────────────────────────────────────────
  const [isPdfViewerVisible, setIsPdfViewerVisible] = useState(true)
  const [leftPanelTab, setLeftPanelTab] = useState('fields')
  const [fieldSearchQuery, setFieldSearchQuery] = useState('')
  const [fieldsExtractionFilter, setFieldsExtractionFilter] = useState('all')
  const [fieldsViewFilter, setFieldsViewFilter] = useState('all')
  const { tabRefs: fieldsViewTabRefs, indicatorStyle: fieldsViewIndicatorStyle } = useSegmentedTabIndicator(fieldsViewFilter, [leftPanelTab])
  const [activityLogs, setActivityLogs] = useState([])
  const [loadingActivity, setLoadingActivity] = useState(false)
  const [activityLoaded, setActivityLoaded] = useState(false)

  const fetchActivityLogs = useCallback(async () => {
    if (!request?.id) return
    try {
      setLoadingActivity(true)
      const res = await requestsApi.getAuditLogs(request.id, { limit: 20 })
      setActivityLogs(formatAuditLogsForActivity(res.data.audit_logs || []))
    } catch (err) {
      console.error('Error fetching audit logs:', err)
      setActivityLogs([])
    } finally {
      setLoadingActivity(false)
      setActivityLoaded(true)
    }
  }, [request?.id])

  useEffect(() => {
    if (leftPanelTab === 'activity' && !activityLoaded) {
      fetchActivityLogs()
    }
  }, [leftPanelTab, activityLoaded, fetchActivityLogs])

  const [leftPanelPct, setLeftPanelPct] = useState(50)
  const [isDraggingDivider, setIsDraggingDivider] = useState(false)
  const mainContentRef = useRef(null)

  const handleDividerMouseDown = (e) => {
    e.preventDefault()
    setIsDraggingDivider(true)

    const onMouseMove = (moveEvent) => {
      if (!mainContentRef.current) return
      const rect = mainContentRef.current.getBoundingClientRect()
      let pct = ((moveEvent.clientX - rect.left) / rect.width) * 100
      pct = Math.min(80, Math.max(20, pct))
      setLeftPanelPct(pct)
    }
    const onMouseUp = () => {
      setIsDraggingDivider(false)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  // Average confidence across all header fields that have a confidence value
  const avgConfidence = useMemo(() => {
    const withConf = headerFields.filter(f => f.confidence != null && f.confidence > 0)
    if (!withConf.length) return null
    return Math.min(100, Math.max(0, Math.round(
      (withConf.reduce((s, f) => s + (f.confidence || 0), 0) / withConf.length) * 100
    )))
  }, [headerFields])

  const pdfDoc = (request?.documents || []).find(d => d.content_type === 'application/pdf' || d.filename?.endsWith('.pdf'))
  const pdfDocId = pdfDoc?.id

  const requestLocked = isRequestLocked(request?.status)
  const canEditFields = !isReader && !requestLocked
  const canEdit = !isReader && !requestLocked
  const notifyLocked = () => toast.info('Read-only', isReader ? 'You have read-only access' : 'This request is finalized and can no longer be edited')

  if (loadingValidation) {
    return (
      <FormSkeleton />
    )
  }

  return (
    <>
    {/* AI Processing overlay */}
    {showProcessingModal && (
      processingMinimized ? (
        <button onClick={() => setProcessingMinimized(false)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all group">
          <div className="relative">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
              <Bot className="h-4 w-4 text-white" />
            </div>
          </div>
          <div className="text-left">
            <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">Processing...</p>
            <p className="text-[10px] text-gray-500 dark:text-gray-400">{processingProgress.percent}% — click to expand</p>
          </div>
          <Maximize2 className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 ml-1" />
        </button>
      ) : (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-navy-900/60 backdrop-blur-sm" onClick={() => setProcessingMinimized(true)} />
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8 max-w-lg w-full mx-4">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-500 opacity-10 animate-pulse" />
            <div className="relative flex flex-col items-center text-center">
              <div className="relative mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                  <Bot className="h-8 w-8 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">AI is Extracting Details</h3>
              <div className="w-full mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{processingProgress.message || 'Starting...'}</span>
                  <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">{processingProgress.percent}%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500" style={{ width: `${Math.max(processingProgress.percent, 2)}%` }} />
                </div>
              </div>
              <div className="w-full bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 max-h-64 overflow-y-auto text-left">
                {processingLog.length === 0 ? (
                  <div className="p-3 flex items-center gap-2 text-xs text-gray-400"><Loader2 className="h-3 w-3 animate-spin" /><span>Waiting…</span></div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {processingLog.map((step, idx) => (
                      <div key={idx} className="px-3 py-1.5 flex items-start gap-2">
                        <span className="text-[10px] font-mono text-gray-400 whitespace-nowrap mt-0.5">{step.elapsed_seconds != null ? `${step.elapsed_seconds}s` : step.timestamp}</span>
                        <span className={`text-xs ${step.message?.includes('✅') ? 'text-green-600' : step.message?.includes('❌') ? 'text-red-600' : 'text-gray-700 dark:text-gray-300'}`}>{step.message}</span>
                      </div>
                    ))}
                    <div ref={el => el?.scrollIntoView({ behavior: 'smooth', block: 'end' })} />
                  </div>
                )}
              </div>
              <p className="text-[10px] text-gray-400 mt-2">Click anywhere outside to minimize</p>
            </div>
          </div>
        </div>
      )
    )}

    <div className="-m-6 flex flex-col bg-gray-100 dark:bg-gray-950 overflow-hidden animate-page-in" style={{ height: 'calc(100vh - 4rem)' }}>

      <UnassignedOpCoBanner request={request} onMoved={onOpCoMoved} />
      {/* Top header bar — same gradient/dot-pattern + icon-badge + title design as the BC/PO/normal extraction pages */}
      <div className="relative bg-gradient-to-r from-navy-100/90 via-navy-50/70 to-navy-100/80 dark:from-gray-900/80 dark:via-gray-800/60 dark:to-gray-900/70 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 animate-header-in">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.06] dark:opacity-[0.08]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, var(--tw-color-navy-900, #0f2942) 1px, transparent 0)',
            backgroundSize: '20px 20px',
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-navy-600 dark:bg-navy-500"
        />
        <div
          className={`relative z-10 pl-6 pr-5 flex items-start justify-between gap-4 flex-wrap transition-[padding] duration-300 ease-out ${
            isHeaderCollapsed ? 'py-2.5' : 'pt-4 pb-3'
          }`}
        >
          {/* LEFT: icon badge + title + template/file chips — collapsible */}
          <div className={`relative flex min-w-0 flex-1 basis-[280px] ${isHeaderCollapsed ? 'items-center gap-3' : 'items-start gap-3.5'}`}>
            <div
              className={`hidden sm:flex flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-600 to-navy-800 text-white shadow-md ring-1 ring-navy-900/10 transition-all ${
                isHeaderCollapsed ? 'h-9 w-9' : 'h-11 w-11'
              }`}
              title="NetSuite document request"
            >
              <FileText className={isHeaderCollapsed ? 'h-4 w-4' : 'h-5 w-5'} />
            </div>
            <div className="min-w-0 flex-1 flex flex-col gap-1.5">
              <div className="flex items-center gap-1.5">
                <h1
                  className="text-lg font-bold tracking-tight text-gray-900 dark:text-gray-100 leading-snug truncate"
                  title="Request title"
                >
                  {pdfDoc?.filename || request?.name || 'Untitled Request'}
                </h1>
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-navy-100 text-navy-700 dark:bg-navy-900/30 dark:text-navy-300 flex-shrink-0"
                  title="NetSuite record type this request will post as"
                >
                  NS {displayLabel}
                </span>
                <button
                  type="button"
                  onClick={() => setIsHeaderCollapsed(prev => !prev)}
                  title={isHeaderCollapsed ? 'Expand details' : 'Collapse details'}
                  className="flex-shrink-0 p-1 rounded-md text-gray-400 dark:text-gray-500 hover:text-navy-600 dark:hover:text-navy-400 hover:bg-navy-50 dark:hover:bg-navy-900/20 transition-colors"
                >
                  <ChevronDown className={`h-4 w-4 transition-transform ${isHeaderCollapsed ? '-rotate-90' : ''}`} />
                </button>
                <OpCoBadge request={request} />
                <MoveOpCoButton request={request} onMoved={onOpCoMoved} />
              </div>
              {!isHeaderCollapsed && (request?.template_name || request?.template?.name || pdfDoc?.filename) && (
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  {(request?.template_name || request?.template?.name) && (
                    <span
                      className="inline-flex items-center gap-1.5 h-6 w-fit max-w-full pl-1 pr-2.5 rounded-full bg-gray-50 dark:bg-gray-700/40 border border-gray-200/70 dark:border-gray-600/50 transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5"
                      title="Extraction template used for this request"
                    >
                      <span className="flex items-center justify-center h-4 w-4 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300 shrink-0">
                        <FileSpreadsheet className="h-2.5 w-2.5" />
                      </span>
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 truncate max-w-[240px]">
                        {request?.template_name || request?.template?.name}
                      </span>
                    </span>
                  )}
                  {pdfDoc?.filename && (
                    <span
                      className="inline-flex items-center gap-1.5 h-6 w-fit max-w-full pl-1 pr-2.5 rounded-full bg-gray-50 dark:bg-gray-700/40 border border-gray-200/70 dark:border-gray-600/50 transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5"
                      title="Source document attached to this request"
                    >
                      <span className="flex items-center justify-center h-4 w-4 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300 shrink-0">
                        <FileText className="h-2.5 w-2.5" />
                      </span>
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 truncate max-w-[240px]">
                        {pdfDoc.filename}
                      </span>
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: AI confidence / status (top row) + action buttons (below) */}
          <div className={`flex flex-shrink-0 gap-2 ${isHeaderCollapsed ? 'flex-row items-center' : 'flex-col items-end'}`}>
            {!isHeaderCollapsed && (
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 shrink min-w-0 whitespace-nowrap">
                {avgConfidence != null && (
                  <span className={`group relative inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border flex-shrink-0 cursor-default ${
                    avgConfidence >= 90
                      ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                      : avgConfidence >= 70
                      ? 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400'
                      : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400'
                  }`}>
                    AI Confidence: <strong>{avgConfidence}%</strong>

                    {/* Confidence legend — shown on hover */}
                    <div className="pointer-events-none absolute right-0 top-full z-20 mt-1.5 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-150">
                      <div className="flex items-center gap-2.5 text-[11px] px-2.5 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-md whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <span className="text-gray-500 dark:text-gray-400 font-normal">&gt;90%</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                          <span className="text-gray-500 dark:text-gray-400 font-normal">70-89%</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                          <span className="text-gray-500 dark:text-gray-400 font-normal">&lt;70%</span>
                        </div>
                      </div>
                    </div>
                  </span>
                )}
                {/* Saved timestamp — informational, rendered even with no confidence data */}
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-700/40 text-gray-400 dark:text-gray-500 flex-shrink-0">
                  <Clock className="h-3.5 w-3.5" />
                  <span className="text-xs whitespace-nowrap">
                    Saved {request?.updated_at ? formatTimeAgo(request.updated_at) : 'N/A'}
                  </span>
                </span>
              </div>
            )}

            <div className="flex items-center gap-2.5 flex-wrap justify-end">
              {canEditFields && request?.can_cancel !== false && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold text-red-600 dark:text-red-300 bg-white dark:bg-gray-900 border border-red-300 dark:border-red-400/40 hover:bg-red-50 hover:border-red-400 dark:hover:bg-red-400/10 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.96]"
                >
                  <X className="h-3.5 w-3.5" />
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div ref={mainContentRef} className={`flex flex-1 overflow-hidden ${isDraggingDivider ? 'select-none cursor-col-resize' : ''}`}>

      {/* LEFT: Fields — fields on the left, document on the right */}
      <section
        style={{ width: isPdfViewerVisible ? `${leftPanelPct}%` : '100%' }}
        className={`min-w-[320px] flex-shrink-0 flex flex-col bg-white dark:bg-gray-900 overflow-hidden ${isDraggingDivider ? '' : 'transition-all duration-150'}`}
      >
        <div className="px-4 border-b border-gray-100 dark:border-gray-700 flex-shrink-0 bg-gradient-to-b from-gray-50/80 to-white dark:from-gray-800/60 dark:to-gray-900 pt-3.5 pb-3.5">
          <RequestPanelTabs
            tabs={[
              { key: 'fields', label: 'Fields', icon: ClipboardList },
              { key: 'activity', label: 'Activity', icon: Activity },
              { key: 'ai_ops', label: 'AI Insights', icon: Sparkles },
            ]}
            activeTab={leftPanelTab}
            onTabChange={setLeftPanelTab}
            isDocViewerVisible={isPdfViewerVisible}
            onToggleDocViewer={() => { setIsPdfViewerVisible(v => !v); setLeftPanelPct(50) }}
          />
          {leftPanelTab === 'fields' && (
            <FieldsPanelHeader
              fieldCount={headerFields.length}
              emptyRequiredCount={missingRequired.length}
              onJumpToEmptyRequired={() => jumpToFieldKeys(missingRequired.map(m => m.key))}
              searchQuery={fieldSearchQuery}
              onSearchChange={setFieldSearchQuery}
              extractionFilter={fieldsExtractionFilter}
              onExtractionFilterChange={setFieldsExtractionFilter}
              extractedCount={
                (fieldsViewFilter === 'mandatory'
                  ? headerFields.filter(f => f.is_required)
                  : fieldsViewFilter === 'custom'
                    ? headerFields.filter(f => f.source_type === 'user_input')
                    : headerFields
                ).filter(f => ['document', 'email_body', 'llm_fallback', 'ai_learning'].includes(f.source_type)).length
              }
              notExtractedCount={
                (fieldsViewFilter === 'mandatory'
                  ? headerFields.filter(f => f.is_required)
                  : fieldsViewFilter === 'custom'
                    ? headerFields.filter(f => f.source_type === 'user_input')
                    : headerFields
                ).filter(f => !f.source_type || f.source_type === 'pending').length
              }
              viewFilter={fieldsViewFilter}
              onViewFilterChange={setFieldsViewFilter}
              viewIndicatorStyle={fieldsViewIndicatorStyle}
              viewTabRefs={fieldsViewTabRefs}
            />
          )}
        </div>

        {leftPanelTab === 'activity' && (
          <ActivityLogPanel logs={activityLogs} loading={loadingActivity} onRefresh={fetchActivityLogs} />
        )}

        {leftPanelTab === 'ai_ops' && (
          <div className="flex-1 overflow-y-auto px-4 py-4 animate-panel-in">
            <h3 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100 mb-3">AI Insights</h3>
            <CostBreakdown requestId={request.id} embedded />
          </div>
        )}

        {leftPanelTab === 'fields' && (
        <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-3 space-y-5 animate-panel-in">

          {/* Unified pre-post checklist — a single collapsible card instead of
              an always-open banner, same design as the PO/BC/Vendor Bid
              request pages. */}
          {!postResult?.success && (() => {
            const lineItemsMissing = hasLineItems && buildLinePayload().length === 0
            const groups = [
              missingRequired.length > 0 && { group: 'Fields', items: missingRequired.map(m => ({ label: m.label, detail: 'Required — not filled in', keys: [m.key] })) },
              lineItemsMissing && { group: 'Line Items', items: [{
                label: 'Line items',
                detail: lineColumnsUnmapped
                  ? 'No line columns mapped — fix in template settings'
                  : 'No line items added',
                keys: ['lines'],
              }] },
            ].filter(Boolean)
            const totalIssues = groups.reduce((n, g) => n + g.items.length, 0)
            const ready = totalIssues === 0
            if (ready) return null
            return (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowChecklist(o => !o)}
                  className="w-full flex items-center gap-2 px-3.5 py-2.5 transition-colors bg-amber-50 dark:bg-amber-900/10 hover:bg-amber-100 dark:hover:bg-amber-900/20"
                >
                  <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                  <p className="text-sm font-semibold flex-1 text-left text-amber-800 dark:text-amber-300">
                    {totalIssues} thing{totalIssues !== 1 ? 's' : ''} to fix before posting
                  </p>
                  <ChevronDown className={`h-4 w-4 text-amber-400 transition-transform flex-shrink-0 ${showChecklist ? 'rotate-180' : ''}`} />
                </button>

                {showChecklist && (
                  <div className="divide-y divide-gray-100 dark:divide-gray-800 animate-panel-in">
                    {groups.map(({ group, items }) => (
                      <div key={group} className="px-3.5 py-2.5">
                        <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
                          {group} ({items.length})
                        </p>
                        <div className="space-y-0.5 -mx-1.5">
                          {items.map((item, i) => {
                            const clickable = item.keys && item.keys.length > 0
                            return (
                              <button
                                key={i}
                                type="button"
                                onClick={() => clickable && jumpToFieldKeys(item.keys)}
                                disabled={!clickable}
                                title={clickable ? `Jump to ${item.label}` : item.label}
                                className={`w-full flex items-start justify-between gap-2 px-1.5 py-1 rounded text-left transition-colors ${
                                  clickable ? 'hover:bg-amber-50 dark:hover:bg-amber-900/20 cursor-pointer' : 'cursor-default'
                                }`}
                              >
                                <span className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1 min-w-0 flex-shrink-0 max-w-[40%]">
                                  <span className="truncate">{item.label}</span>
                                  {clickable && <ChevronRight className="h-3 w-3 text-amber-400 flex-shrink-0" />}
                                </span>
                                <span className="text-xs font-medium text-amber-700 dark:text-amber-300 text-right">{item.detail}</span>
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })()}

          {headerFields.length === 0 && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-800 dark:text-amber-300">
              <p className="font-semibold">No extracted request fields found for this record.</p>
              <p className="mt-1">This can happen when extraction failed or the record was posted manually. Add required values and post again to save data into this request.</p>
            </div>
          )}

          {(() => {
            let visibleFields = headerFields
            if (fieldsViewFilter === 'mandatory') {
              visibleFields = visibleFields.filter(f => f.is_required)
            } else if (fieldsViewFilter === 'custom') {
              visibleFields = visibleFields.filter(f => f.source_type === 'user_input')
            }
            if (fieldsExtractionFilter === 'extracted') {
              visibleFields = visibleFields.filter(f => ['document', 'email_body', 'llm_fallback', 'ai_learning'].includes(f.source_type))
            } else if (fieldsExtractionFilter === 'not_extracted') {
              visibleFields = visibleFields.filter(f => !f.source_type || f.source_type === 'pending')
            }
            if (fieldSearchQuery.trim()) {
              const q = fieldSearchQuery.toLowerCase()
              visibleFields = visibleFields.filter(f => (labelFor(f) || '').toLowerCase().includes(q))
            }
            return visibleFields
          })().map(field => {
            const key = field.ns_field
            const label = labelFor(field)
            const val = headerValues[key] ?? ''
            const isEmpty = usesPicker(key) ? !resolvedRefs[key]?.id : (val === '' || val === null)
            const isRequired = field.is_required

            const confidence = field.confidence || 0
            const confidencePercent = Math.round(confidence * 100)
            const isLowConf = confidence > 0 && confidence < 0.7
            const isMedConf = confidence >= 0.7 && confidence < 0.9
            const isHighConf = confidence >= 0.9

            const alternativeCount = field.alternative_count || 0
            const hasAlternatives = alternativeCount >= 1
            const isAlternativesOpen = openAlternativesFieldId === field.id
            const isAuditOpen = openAuditFieldId === field.id

            // Amber, never green: the value is real and will post, but it came
            // from configuration rather than from this document, and the
            // reviewer is the one who has to decide that is correct here.
            const fromDefault = isDefaulted(key)

            // Authoritative per-field complaint from the backend's own
            // validation pass. Local emptiness checks cannot see things like
            // a malformed date or a reference NetSuite refuses to accept.
            const hasServerError = serverFieldErrors.has(key)

            const getFieldBorderColor = () => {
              // Red, not amber: a required field left empty BLOCKS the post,
              // while amber below means "we filled this from a template
              // default, check it" — which does not block. Rendering both
              // the same colour told the reviewer those two states were
              // equivalent when they are opposites.
              if (isRequired && isEmpty) return 'border-2 border-red-400 dark:border-red-500 focus:ring-red-400 hover:ring-2 hover:ring-red-300 dark:hover:ring-red-700'
              if (hasServerError) return 'border-2 border-red-400 dark:border-red-500 focus:ring-red-400 hover:ring-2 hover:ring-red-300 dark:hover:ring-red-700'
              if (fromDefault) return 'border-2 border-amber-400 dark:border-amber-500 focus:ring-amber-400 hover:ring-2 hover:ring-amber-300 dark:hover:ring-amber-700'
              if (!showColorCoding || isEmpty) return 'border-gray-300 dark:border-gray-700 focus:ring-indigo-400'
              if (isLowConf) return 'border-2 border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20 focus:ring-red-400 hover:ring-2 hover:ring-red-300 dark:hover:ring-red-700'
              if (isMedConf) return 'border-2 border-orange-300 bg-orange-50 dark:border-orange-700 dark:bg-orange-900/20 focus:ring-orange-400 hover:ring-2 hover:ring-orange-300 dark:hover:ring-orange-700'
              return 'border-gray-300 dark:border-gray-700 focus:ring-indigo-400'
            }

            return (
              <div
                key={field.id}
                ref={el => { fieldRowRefs.current[key] = el }}
                onClick={() => handleViewFieldInDocument(field)}
                className={`relative space-y-1 p-1 -m-1 cursor-pointer transition-shadow ${jumpHighlightClass(key)}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 min-w-0">
                    {showColorCoding && !isEmpty && confidence > 0 && (
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isHighConf ? 'bg-green-500' : isMedConf ? 'bg-orange-500' : 'bg-red-500'}`} />
                    )}
                    <span className="truncate">{label}</span>
                    {isRequired && <span className="text-red-500">*</span>}
                    <span className="font-mono text-[10px] px-1 rounded text-gray-400 bg-gray-100 dark:bg-gray-800">{field.ns_field}</span>
                    {fromDefault && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 whitespace-nowrap"
                        title="This document did not state a value. It will be posted with this template's default."
                      >
                        template default
                      </span>
                    )}
                    {showConfidenceScore && !isEmpty && confidence > 0 && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${isHighConf ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : isMedConf ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>{confidencePercent}%</span>
                    )}
                    {showAiBadge && !isEmpty && field.source_type === 'llm_fallback' && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">AI</span>
                    )}
                  </label>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleToggleFieldAudit(field) }}
                      className={`flex-shrink-0 p-1 rounded-md transition-all ${isAuditOpen ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-300'}`}
                      title="View edit history"
                    >
                      <History className="h-3.5 w-3.5" strokeWidth={isAuditOpen ? 2.75 : 2} />
                    </button>
                    {hasAlternatives && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleToggleAlternatives(field) }}
                        className={`flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium transition-all border ${isAlternativesOpen ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}
                        title={`${alternativeCount} alternative value(s) from different documents`}
                      >
                        <Layers className="h-3 w-3" />
                        <span>{alternativeCount + 1}</span>
                      </button>
                    )}
                  </div>
                </div>

                {usesPicker(key) ? (
                  <EditGate isEditMode={canEdit} onBlockedClick={notifyLocked}>
                    <ReferenceFieldInput
                      lookupEntity={lookupEntityFor(key)}
                      extractedValue={field.value}
                      orgId={orgId}
                      disabled={!canEdit}
                      onResolved={(id, name) => {
                        setResolvedRefs(prev => ({ ...prev, [key]: { id, name } }))
                        // A value the user picked, or that extraction resolved,
                        // is no longer the template's default.
                        setDefaultedFields(prev => {
                          if (!prev.has(key)) return prev
                          const n = new Set(prev); n.delete(key); return n
                        })
                      }}
                      onCleared={() => {
                        touchedHeaderFieldsRef.current.add(key)
                        setResolvedRefs(prev => { const n = { ...prev }; delete n[key]; return n })
                        setDefaultedFields(prev => {
                          if (!prev.has(key)) return prev
                          const n = new Set(prev); n.delete(key); return n
                        })
                      }}
                      fieldKey={key}
                      activeField={activeReferenceField}
                      onActivate={setActiveReferenceField}
                      fullOptions={fullOptionsFor(key)}
                      presetResolved={fromDefault ? resolvedRefs[key] : undefined}
                    />
                  </EditGate>
                ) : inputTypeFor(field) === 'checkbox' ? (
                  <label
                    className={`flex items-center gap-2 w-full px-3 py-1.5 rounded-lg border text-sm bg-gray-50 dark:bg-gray-800/60 transition-colors duration-200 ${getFieldBorderColor()}`}
                    onClick={e => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={val === true || val === 'true' || val === 'T' || val === 1 || val === '1'}
                      disabled
                      className="h-4 w-4 rounded border-gray-300 text-navy-700 focus:ring-navy-500"
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {(val === true || val === 'true' || val === 'T' || val === 1 || val === '1') ? 'Yes' : 'No'}
                    </span>
                  </label>
                ) : (
                  <input
                    type={inputTypeFor(field) === 'date' ? 'date' : inputTypeFor(field) === 'number' ? 'number' : 'text'}
                    // A date input only renders ISO; the document's D/M/YYYY
                    // (which the server converts the same way at post time)
                    // would otherwise display as an empty box.
                    value={inputTypeFor(field) === 'date' ? toIsoDateForInput(val) : val}
                    readOnly
                    onClick={e => e.stopPropagation()}
                    className={`w-full px-3 py-1.5 rounded-lg border text-sm focus:outline-none focus:ring-1 transition-colors duration-200 bg-gray-50 dark:bg-gray-800/60 cursor-text ${getFieldBorderColor()}`}
                  />
                )}

                {/* Alternatives popup */}
                {isAlternativesOpen && (
                  <div
                    ref={alternativesPopupRef}
                    onClick={e => e.stopPropagation()}
                    className="absolute z-50 top-full right-0 mt-1 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                    style={{ width: '320px' }}
                  >
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        Select value <span className="text-gray-400">({alternatives.length + 1} found)</span>
                      </span>
                      <button onClick={() => setOpenAlternativesFieldId(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {loadingAlternatives ? (
                      <div className="p-6 text-center">
                        <RefreshCw className="h-4 w-4 animate-spin mx-auto text-blue-500" />
                        <span className="text-xs text-gray-500 mt-2 block">Loading...</span>
                      </div>
                    ) : (
                      <div className="overflow-y-auto max-h-64 divide-y divide-gray-100 dark:divide-gray-700">
                        {[{ id: field.id, field_value: field.value, confidence: field.confidence, source_document_filename: field.source_document_filename, page_number: field.page_number, isActive: true }, ...alternatives.map(a => ({ ...a, isActive: false }))].map(item => (
                          <div
                            key={item.id}
                            className={`px-3 py-2.5 transition-all cursor-pointer flex items-center gap-3 ${item.isActive ? 'bg-green-50 dark:bg-green-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                            onClick={() => !item.isActive && handleSelectAlternative(field, item)}
                          >
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${item.isActive ? 'border-green-500 bg-green-500' : 'border-gray-300 hover:border-blue-500'}`}>
                              {item.isActive && <Check className="h-2.5 w-2.5 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm truncate ${item.isActive ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}>
                                {item.field_value || item.extracted_value || '(empty)'}
                              </p>
                              <p className="text-xs text-gray-400 truncate mt-0.5">
                                {item.source_document_filename || 'Unknown source'}
                                {item.page_number && <span className="ml-1">• p.{item.page_number}</span>}
                              </p>
                            </div>
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${(item.confidence || 0) >= 0.9 ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : (item.confidence || 0) >= 0.7 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`}>
                              {Math.round((item.confidence || 0) * 100)}%
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Audit history popup */}
                {isAuditOpen && (
                  <AnchoredPopover
                    ref={auditPopupRef}
                    onClick={e => e.stopPropagation()}
                    anchorClassName="top-full right-0"
                    className="bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden"
                    style={{ width: '340px' }}
                  >
                    <div className="px-3.5 py-2.5 bg-gradient-to-r from-navy-600 to-navy-700 flex items-center justify-between">
                      <span className="text-xs font-semibold text-white flex items-center gap-1.5">
                        <History className="h-3.5 w-3.5" /> Edit History
                      </span>
                      <button onClick={() => setOpenAuditFieldId(null)} className="text-white/70 hover:text-white p-1 hover:bg-white/10 rounded-md transition-colors">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {loadingFieldAudit ? (
                      <div className="p-6 text-center">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto text-navy-500" />
                        <span className="text-xs text-gray-500 dark:text-gray-400 mt-2 block">Loading history...</span>
                      </div>
                    ) : fieldAuditLogs.length === 0 ? (
                      <div className="p-6 text-center">
                        <History className="h-6 w-6 mx-auto text-gray-300 dark:text-gray-600 mb-1.5" />
                        <p className="text-xs text-gray-500 dark:text-gray-400">No edit history found</p>
                      </div>
                    ) : (
                      <div className="overflow-y-auto max-h-64 divide-y divide-gray-100 dark:divide-gray-700">
                        {fieldAuditLogs.map((log, idx) => {
                          let oldValue = null, newValue = null
                          try {
                            const changes = typeof log.audit_json === 'string' ? JSON.parse(log.audit_json) : log.audit_json
                            Object.keys(changes || {}).forEach(k => {
                              if (changes[k].old !== undefined || changes[k].new !== undefined) {
                                oldValue = changes[k].old
                                newValue = changes[k].new
                              }
                            })
                          } catch { /* ignore malformed audit_json */ }
                          const actorName = log.created_by_name || log.created_by || 'System'
                          return (
                            <div key={log.id || idx} className="px-3.5 py-2.5">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">{actorName}</span>
                                <span className="ml-auto text-[11px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                                  {log.created_at ? formatDateTime(log.created_at) : ''}
                                </span>
                              </div>
                              <div className="space-y-1">
                                {oldValue !== null && oldValue !== undefined && (
                                  <div className="flex items-start gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-red-500 dark:text-red-400 w-8 flex-shrink-0 pt-0.5">From</span>
                                    <span className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40 px-2 py-0.5 rounded-md break-all">
                                      {String(oldValue) || '(empty)'}
                                    </span>
                                  </div>
                                )}
                                {newValue !== null && newValue !== undefined && (
                                  <div className="flex items-start gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 w-8 flex-shrink-0 pt-0.5">To</span>
                                    <span className="text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/40 px-2 py-0.5 rounded-md break-all">
                                      {String(newValue) || '(empty)'}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </AnchoredPopover>
                )}

                {isRequired && isEmpty && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3 flex-shrink-0" /> Required</p>
                )}
                {!isEmpty && isLowConf && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3 flex-shrink-0" /> Low confidence — please verify</p>
                )}
              </div>
            )
          })}
        </div>

        {/* ── Line items ── */}
        {hasLineItems && (
          <div
            ref={el => { fieldRowRefs.current['lines'] = el }}
            className={`px-4 pt-3 pb-4 border-t border-gray-100 dark:border-gray-800 flex-shrink-0 transition-shadow ${jumpHighlightClass('lines')}`}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Line items</p>
            </div>
            {lineColumnsUnmapped ? (
              <div className="rounded-lg border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                  No line columns are mapped for this record type
                </p>
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400 leading-snug">
                  {recordTypeKey} posts its lines under
                  {lineContainerKey ? <span className="font-mono"> {lineContainerKey}</span> : ' an embedded sublist'},
                  but this template has no fields mapped to line columns — so no rows can be built
                  from the document. Map the line fields in this template's NetSuite settings,
                  then re-extract this document.
                </p>
              </div>
            ) : (
            <EditGate isEditMode={canEdit} onBlockedClick={notifyLocked}>
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60">
                      {lineFieldDefs.map(f => <th key={f.ns_field} className="px-2 py-1.5 font-medium">{lineLabelFor(f)}</th>)}
                      <th className="px-2 py-1.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {lineRows.map((row, idx) => (
                      <tr key={idx} className="border-t border-gray-100 dark:border-gray-800">
                        {lineFieldDefs.map(f => {
                          const cellValue = row[f.ns_field]
                          if (lineUsesPicker(f.ns_field)) {
                            const rowFieldKey = `line-${idx}-${f.ns_field}`
                            return (
                              <td key={f.ns_field} className="px-2 py-1.5 min-w-[160px]">
                                <ReferenceFieldInput
                                  lookupEntity={lineLookupEntityFor(f.ns_field)}
                                  extractedValue={typeof cellValue === 'object' ? cellValue?.name : cellValue}
                                  orgId={orgId}
                                  disabled={!canEdit}
                                  onResolved={(id, name) => setLineValue(idx, f.ns_field, { id, name })}
                                  onCleared={() => setLineValue(idx, f.ns_field, '')}
                                  fieldKey={rowFieldKey}
                                  activeField={activeReferenceField}
                                  onActivate={setActiveReferenceField}
                                  fullOptions={lineFullOptionsFor(f.ns_field)}
                                />
                              </td>
                            )
                          }
                          // Blank cell on an inherited column: show what the
                          // header will supply, muted, so the posted value is
                          // visible rather than a surprise.
                          const blankCell = cellValue === undefined || cellValue === null || String(cellValue).trim() === ''
                          const inherited = blankCell && f.inherited_from_header ? inheritedValueFor(f) : undefined
                          const inheritedText = inherited && typeof inherited === 'object' ? (inherited.name || inherited.id) : inherited
                          return (
                            <td key={f.ns_field} className="px-2 py-1.5">
                              <input
                                type="text"
                                value={cellValue ?? ''}
                                placeholder={inheritedText ? `${inheritedText} (from header)` : undefined}
                                title={inheritedText ? 'Inherited from the header field of the same name' : undefined}
                                readOnly
                                className="w-full px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                              />
                            </td>
                          )
                        })}
                        <td className="px-2 py-1.5" />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </EditGate>
            )}
          </div>
        )}

        {/* ── Post result + post/retry ── */}
        <div className="px-4 pt-3 pb-5 border-t border-gray-100 dark:border-gray-800 flex-shrink-0 space-y-2.5">
          {postedRecord && !postResult?.success && (
            <div className="rounded-lg border px-4 py-3 flex items-start gap-2 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-green-700 dark:text-green-400">
                Posted to NetSuite — internal id <span className="font-mono font-semibold">{postedRecord.ns_internal_id}</span>
                {postedRecord.ns_url && (
                  <a href={postedRecord.ns_url} target="_blank" rel="noopener noreferrer" className="ml-2 inline-flex items-center gap-1 underline hover:text-green-800 dark:hover:text-green-200">
                    Open in NetSuite <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </p>
            </div>
          )}
          {postResult && (
            <div className={`rounded-lg border px-4 py-3 flex items-start gap-2 ${postResult.success ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'}`}>
              {postResult.success ? <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> : <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />}
              <div className="text-sm">
                {postResult.success ? (
                  <p className="text-green-700 dark:text-green-400">
                    Posted — NetSuite internal id <span className="font-mono font-semibold">{postResult.ns_internal_id}</span>
                    {hasLineItems && <> ({postResult.lines_created} of {postResult.lines_total} lines)</>}
                    {postResult.ns_url && (
                      <a href={postResult.ns_url} target="_blank" rel="noopener noreferrer" className="ml-2 inline-flex items-center gap-1 underline hover:text-green-800 dark:hover:text-green-200">
                        Open in NetSuite <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </p>
                ) : (
                  <>
                    <p className="text-red-600 dark:text-red-400">{postResult.error}</p>
                    {postResult.partial && (
                      <p className="text-red-500 dark:text-red-400 mt-1">
                        Header created (id {postResult.ns_internal_id}) — {postResult.lines_created} of {postResult.lines_total} lines posted. Retrying only completes what's missing, the header is never recreated.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {canEdit && serverValidation.status === 'invalid' && (serverValidation.errors || []).length > 0 && (
            <div className="mb-2 rounded-lg border border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-900/20 px-3 py-2">
              <p className="text-xs font-semibold text-red-700 dark:text-red-300 flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                NetSuite would reject this record
              </p>
              <ul className="mt-1 space-y-0.5">
                {(serverValidation.errors || []).slice(0, 6).map((err, i) => (
                  <li key={i} className="text-xs text-red-700 dark:text-red-300 leading-snug">
                    • {err?.message || err?.field_key || 'Validation error'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {canEdit && (
            <button
              onClick={handlePost}
              disabled={!canPost}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-navy-700 hover:bg-navy-800 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98] shadow-sm hover:shadow"
            >
              {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : postGate.reason === 'checking' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {postGate.reason === 'checking' ? 'Checking with NetSuite…' : 'Post to NetSuite'}
            </button>
          )}
          {canEdit && !canPost && !posting && postGate.reason !== 'invalid' && postGate.reason !== 'checking' && gateMessage(postGate, missingRequired) && (
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 text-center">
              {gateMessage(postGate, missingRequired)}
            </p>
          )}
          {canEdit && postResult && !postResult.success && (
            <button
              onClick={handleRetry}
              disabled={posting}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 transition-all duration-200 active:scale-[0.98]"
            >
              {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />}
              Retry
            </button>
          )}
        </div>
        </div>
        )}
      </section>

      {/* Draggable divider — drag to resize Fields vs document panels */}
      <div
        onMouseDown={isPdfViewerVisible ? handleDividerMouseDown : undefined}
        role="separator"
        aria-orientation="vertical"
        title="Drag to resize"
        className={`relative flex-shrink-0 bg-gray-200 dark:bg-gray-700 hover:bg-teal-400 dark:hover:bg-teal-500 active:bg-teal-500 group z-10 overflow-hidden ${
          isDraggingDivider ? '' : 'transition-[width,opacity] duration-300 ease-out'
        } ${isPdfViewerVisible ? 'w-1 opacity-100 cursor-col-resize' : 'w-0 opacity-0 pointer-events-none'}`}
      >
        <div className="absolute inset-y-0 -left-1.5 -right-1.5" />
      </div>

      {/* RIGHT: Document viewer */}
      <div
        style={{ width: isPdfViewerVisible ? `${100 - leftPanelPct}%` : '0%' }}
        className={`flex-shrink-0 border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col overflow-hidden ${
          isDraggingDivider ? '' : 'transition-[width,opacity] duration-300 ease-out'
        } ${isPdfViewerVisible ? 'min-w-[300px] opacity-100' : 'min-w-0 opacity-0 pointer-events-none'}`}
      >
        <div className="flex-1 overflow-hidden">
          {pdfDocId ? <PDFViewer documentId={pdfDocId} highlightedField={highlightedField} /> : (
            <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-400">
              <Upload className="h-8 w-8 opacity-30" />
              <p className="text-sm">No PDF available</p>
            </div>
          )}
        </div>
      </div>

      </div>{/* end two-panel body */}
    </div>{/* end page */}

    {/* ── Cancel Request Modal ── */}
    {showCancelModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowCancelModal(false)} />
        <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full mx-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
              <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Cancel Request</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">This action cannot be undone.</p>
            </div>
          </div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Reason for cancellation <span className="text-red-500">*</span></label>
          <textarea
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            rows={3}
            placeholder="Enter reason…"
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
          />
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => { setShowCancelModal(false); setCancelReason('') }}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Keep Request
            </button>
            <button
              onClick={handleCancel}
              disabled={!cancelReason.trim() || cancelling}
              className="flex-1 px-4 py-2 text-sm font-semibold rounded-lg bg-red-500 hover:bg-red-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
            >
              {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
              {cancelling ? 'Cancelling…' : 'Cancel Request'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  )
}
